/**
 * The game's scheduled end: the countdown to it, and the end times it can be
 * moved to. Times of day are "HH:mm" in the browser's time zone, like the
 * event form's TimePicker.
 */

type TimeOption = { label: string; value: string };

/** Milliseconds until `endTime` (ISO), never negative; null with no end. */
export const getRemainingMs = (
  endTime: string | undefined,
  now: number,
): number | null => {
  if (!endTime) return null;
  const endMs = Date.parse(endTime);
  if (!Number.isFinite(endMs)) return null;
  return Math.max(0, endMs - now);
};

/** "89:04": whole minutes, then seconds. Minutes don't roll into hours. */
export const formatRemaining = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

/** "HH:mm" for an epoch ms time. */
export const toTimeOfDay = (ms: number) => {
  const date = new Date(ms);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

/** `timeOfDay` ("HH:mm") on the day of `dayMs`, in epoch ms. */
export const atTimeOfDay = (dayMs: number, timeOfDay: string) => {
  const [hours, minutes] = timeOfDay.split(":").map(Number);
  const date = new Date(dayMs);
  date.setHours(hours, minutes, 0, 0);
  return date.getTime();
};

/** "4:00 PM" for an epoch ms time. */
export const formatTimeOfDay = (ms: number) =>
  new Date(ms).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

/**
 * What moving the end does to the time remaining, e.g. "Saving this new end
 * time will add 30 minutes to the time remaining". Null when it doesn't move.
 */
export const describeEndTimeChange = (
  currentEndMs: number,
  newEndMs: number,
): string | null => {
  const minutes = Math.round((newEndMs - currentEndMs) / 60_000);
  if (minutes === 0) return null;
  const count = Math.abs(minutes);
  const amount = `${count} ${count === 1 ? "minute" : "minutes"}`;
  const verb = minutes > 0 ? "add" : "deduct";
  return `Saving this new end time will ${verb} ${amount} to the time remaining`;
};

/**
 * The end times to offer: those still ahead of `now` on the end's day, plus
 * the current end (which can be off the picker's 15 minute steps).
 */
export const getEndTimeOptions = (
  options: TimeOption[],
  currentEndMs: number,
  now: number,
): TimeOption[] => {
  const current = toTimeOfDay(currentEndMs);
  const isEndToday =
    new Date(currentEndMs).toDateString() === new Date(now).toDateString();
  const earliest = isEndToday ? toTimeOfDay(now) : "";
  const ahead = options.filter((option) => option.value > earliest);
  if (ahead.some((option) => option.value === current)) return ahead;
  return [
    ...ahead,
    { value: current, label: formatTimeOfDay(currentEndMs) },
  ].sort((a, b) => a.value.localeCompare(b.value));
};
